import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

/**
 * Global ATS account credentials. ONE pair per desktop install — the
 * worker uses these whenever a job application requires creating an
 * account on the ATS (Workday today; Greenhouse / Lever / Ashby on
 * the roadmap, see #375 / #376 / #377). Sign-in is attempted first
 * against the saved email; if no account exists yet, the worker
 * creates one using the saved password through the ATS's standard
 * create-account flow (including any email-link activation step).
 *
 * Stored in macOS Keychain on the desktop only (#392). The pre-#392
 * cloud surface (DDB row, /v1/me/ats-account API, mobile screen) is
 * deleted — credentials never leave this Mac, matching the project's
 * existing rule for LinkedIn credentials (see CLAUDE.md Security +
 * Credential Rules). The schema is kept so the desktop Keychain
 * wrapper at `apps/desktop-electron/src/main/keychain.ts` can validate
 * stored JSON before handing it to the dispatcher.
 *
 * Per-tenant `WorkdayTenantCreds` (also Keychain-only post-#392) stays
 * as an OPTIONAL Workday-specific override (different feature — user
 * has a pre-existing real account at acme.workday.com). The dispatcher
 * tries per-tenant first, falls back to global ATS account, falls back
 * to `atsCredentialsMissing`.
 */
export const AtsAccount = z.object({
  userId: UserId,
  /** Email the user wants associated with all account-creation flows. */
  email: z.string().email().max(254),
  /** Password the worker uses for both sign-in and create-account. */
  password: z.string().min(1).max(256),
  updatedAt: IsoTimestamp,
});
export type AtsAccount = z.infer<typeof AtsAccount>;
